// The schedules everybody builds by hand on the way in, ready-made.
//
// An example here is a SPEC, not a document: it stamps the same ordinary `GI.*`
// formulas the pane stamps, at the selected cell, over whatever is already on
// the sheet. Nothing is replaced and nothing is owned — what lands is cells to
// edit, which is the point of offering a starting point at all.
//
// ADDING ONE is appending an entry to `DEFS`. Nothing in the toolbar or the
// pane changes: both render whatever `scheduleExamples` returns, in this order.
//
// A definition names the columns it WANTS. The ones the project has no field
// for are dropped, and a definition left with nothing is not offered at all —
// an example that stamps an empty rectangle is worse than no example, because
// it teaches people to ignore the menu it is in.

import { prettyField } from '@/data/classify';
import { naturalCompare } from '@/model/collate';
import type { GiraffeData } from '@/data/types';
import type { SourceKind } from '@/model/types';
import type { ScheduleMeasure, ScheduleSpec } from '@/giraffe/stamp';

export interface ScheduleExample {
  id: string;
  /** Menu text. A name, not a sentence. */
  label: string;
  /** Hover text — what it stamps, in words. */
  title: string;
  spec: ScheduleSpec;
}

interface ExampleDef {
  id: string;
  label: string;
  /** Given the columns that survived, say what this stamps. */
  title(columns: ScheduleMeasure[]): string;
  source?: SourceKind;
  groupBy?: string | null;
  rows?: number;
  showTotal?: boolean;
  /** The columns it wants, in order. Filtered against the project's fields. */
  columns(fields: ReadonlySet<string>, data: GiraffeData): ScheduleMeasure[];
}

/**
 * The bed-type columns, as the engine names them: `dwelling-2BED-count`.
 *
 * The dwelling mix arrives as ONE FIELD PER BED TYPE, not as values of a "bed
 * type" column — so a unit mix is measures-as-columns, and a cross-tab is the
 * wrong shape for it.
 */
export function mixFields(data: GiraffeData): string[] {
  return data
    .fields('bakedSections')
    .filter((f) => /^dwelling-.+-count$/i.test(f))
    .sort((a, b) => naturalCompare(a, b));
}

/** Drops the columns whose field this project does not have. */
function present(fields: ReadonlySet<string>, columns: ScheduleMeasure[]): ScheduleMeasure[] {
  return columns.filter((m) => fields.has(m.field));
}

const DEFS: ExampleDef[] = [
  {
    id: 'dwelling-counts',
    label: 'Dwelling counts',
    title: (cols) =>
      cols.length > 1
        ? `Rows by usage, one column per bed type (${cols.length - 1}), and a total`
        : 'Dwelling counts by usage',
    columns: (fields, data) =>
      present(fields, [
        ...mixFields(data).map((field) => ({
          field,
          agg: 'sum' as const,
          label: prettyField(field.replace(/^dwelling-|-count$/gi, '')),
        })),
        { field: 'dwellingTotalCount', agg: 'sum', label: 'Total' },
      ]),
  },
  {
    id: 'area-schedule',
    label: 'Area schedule',
    title: () => 'Rows by usage — gross, net, saleable and efficiency',
    // No Footprint/`area` column — user-requested removal: `area` is a
    // `plan` field (one level's footprint, the same number on every row of
    // a level-split stack), and summing one through GI.TOTAL/GI.FOOTPRINT
    // now overcounts a level-split stack exactly as a hand-written Excel
    // SUM over the same column would (see areasTable.ts's `mergeAreaRows`
    // doc comment) — not something this starting point should invite.
    // `grossArea` already has levels applied, so it sums cleanly either way.
    // Efficiency is a rate, so `auto` takes its area-weighted mean rather
    // than adding percentages up.
    columns: (fields) =>
      present(fields, [
        { field: 'grossArea', agg: 'auto', label: 'Gross' },
        { field: 'netArea', agg: 'auto', label: 'Net' },
        { field: 'saleableArea', agg: 'auto', label: 'Saleable' },
        { field: 'efficiency', agg: 'auto', label: 'Efficiency', fmt: 'percent0' },
      ]),
  },
];

/** Only the examples this project has the fields to fill. */
export function scheduleExamples(data: GiraffeData): ScheduleExample[] {
  const out: ScheduleExample[] = [];
  for (const def of DEFS) {
    const source = def.source ?? 'bakedSections';
    const columns = def.columns(new Set(data.fields(source)), data);
    if (columns.length === 0) continue;
    out.push({
      id: def.id,
      label: def.label,
      title: def.title(columns),
      spec: {
        source,
        groupBy: def.groupBy ?? 'usage',
        pivotBy: null,
        measures: columns,
        filters: [],
        rows: def.rows ?? 10,
        showHeader: true,
        showTotal: def.showTotal ?? true,
      },
    });
  }
  return out;
}
